// Load the firebase namespace along with all its type information (but no actual code)
import firebase from "firebase/compat/app"; // Cannot go to the modular, until ALL parts go to the modular. The compat sets up firebase for BOTH the modular and non-modular ("/compat/") services for auth, database and storage. DATABASE is the sticking point as we are waiting for VueFire to upgrade to Firebase API v9 (As of Jan 2022, not doing it yet)

// Load individual services
import {
  connectAuthEmulator,
  getAuth,
  GoogleAuthProvider,
  signInWithCustomToken,
  signOut,
} from "firebase/auth";
import "firebase/compat/database"; // CANNOT move this to the new format yet, because VueFire is not yet upgraded to Firebase API v9
import {
  connectStorageEmulator,
  getStorage,
  ref as firebaseCloudStorageRefFunction /* to disambiguate from the 'ref' of database */,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import {
  connectFunctionsEmulator,
  getFunctions,
  httpsCallable,
} from "firebase/functions";

import { getSha1HexFromStringP } from "./sha1.mjs";
import { withoutQueryString } from "./urls.mjs";

const firebaseConfig = {
  apiKey: "AIzaSyCDkJ3vGgHTkbUsW2Ab_GbHHzmeMzsFwq8",
  authDomain: "inspirion.org",
  // authDomain: "inspirion-33fec.firebaseapp.com",
  databaseURL: "https://inspirion-33fec.firebaseio.com",
  projectId: "inspirion-33fec",
  storageBucket: "inspirion-33fec.appspot.com",
  messagingSenderId: "605205424042",
  appId: "1:605205424042:web:1b2d3ec07e4e51b352add3",
};
const firebaseApp = firebase.initializeApp(firebaseConfig);

// Same bucket has two access URLs
const BUCKET_ADDRESS_VIA_FIREBASE_CLOUD = `firebasestorage.googleapis.com/v0/b/${firebaseConfig.storageBucket}/o`; // Old way. Has a TOKEN suffix which is bad because (1) reuploading same image gets new token, so old image will no longer display, (2) browser can't cache
const BUCKET_ADDRESS_VIA_GOOGLE_CLOUD = `${firebaseConfig.storageBucket}.storage.googleapis.com`; // New way
const UNSPLASH_ADDRESS = "source.unsplash.com/";
const UNITY_MAGIQUANT_ADDRESS = "files.magiquant.com/";
const UNITY_GOOGLEAPIS_ADDRESS = "storage.googleapis.com/scantensus/";
// Set by `npm run serve:emulator` (.env.emulator); see the Database section.
const isUsingFirebaseEmulator = process.env.VUE_APP_FIREBASE_EMULATOR === "true";
// In emulator mode, pasted images are uploaded to the Storage emulator and
// saved with its address, which must then count as a valid place for them.
const BUCKET_ADDRESS_VIA_STORAGE_EMULATOR = `127.0.0.1:9199/v0/b/${firebaseConfig.storageBucket}/o/`;
const ADDRESSES = [
  BUCKET_ADDRESS_VIA_FIREBASE_CLOUD,
  BUCKET_ADDRESS_VIA_GOOGLE_CLOUD,
  UNSPLASH_ADDRESS,
  UNITY_MAGIQUANT_ADDRESS,
  UNITY_GOOGLEAPIS_ADDRESS,
  ...(isUsingFirebaseEmulator ? [BUCKET_ADDRESS_VIA_STORAGE_EMULATOR] : []),
];

const HTTP = "http://";
const HTTPS = "https://";
const TRANSPORTS = [HTTP, HTTPS];

export const VALID_IMAGE_STORAGE_URL_PREFIXES = [
  ...TRANSPORTS.map((transport) =>
    ADDRESSES.map((address) => `${transport}${address}`)
  ),
].flat();

/*
 
   _____        _        _                    
  |  __ \      | |      | |                   
  | |  | | __ _| |_ __ _| |__   __ _ ___  ___ 
  | |  | |/ _` | __/ _` | '_ \ / _` / __|/ _ \
  | |__| | (_| | || (_| | |_) | (_| \__ \  __/
  |_____/ \__,_|\__\__,_|_.__/ \__,_|___/\___|
                                              
                                              
 
*/

export const db = firebaseApp.database();
export const auth = getAuth(firebaseApp);

// WARNING: this must stay above the first db.ref(): creating a ref
// initialises the database, after which useEmulator() throws and the app
// silently talks to the live project.
if (isUsingFirebaseEmulator) {
  db.useEmulator("127.0.0.1", 9000);
  connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
  // Storage and Functions are pointed at their emulator ports even when those
  // emulators are not started: a call in emulator mode should fail, not reach
  // the live bucket or functions.
  connectStorageEmulator(getStorage(), "127.0.0.1", 9199);
  connectFunctionsEmulator(getFunctions(), "127.0.0.1", 5001);
  // End-to-end tests sign in with a custom token minted by firebase-admin
  // against the Auth emulator, which is what cy.login did for Cypress.
  window.__signInToEmulatorWithCustomTokenP = (customToken) =>
    signInWithCustomToken(auth, customToken);
  window.__signOutOfEmulatorP = () => signOut(auth);
}

export const firebaseDatabaseServerValue = firebase.database.ServerValue; // NOTHING to do with firebase*App*.database*()*
export const systemStatusRef = db.ref("systemStatus");
export const provider = new GoogleAuthProvider();

export const firebaseRoot = "/Inspirion";

export const usersPath = `${firebaseRoot}/users/`;
export const questionsPath = `${firebaseRoot}/questions/`;

export const firstGuessesPath = `${firebaseRoot}/questionFirstGuesses/`;
export const firstGuessesHistoPath = `${firebaseRoot}/questionFirstGuessesHisto/`;
export const firstGuessesTimelinePath = `${firebaseRoot}/questionFirstGuessesTimeline/`;

export const mostRecentGuessesPath = `${firebaseRoot}/questionMostRecentGuesses/`;
export const mostRecentGuessesHistoPath = `${firebaseRoot}/questionMostRecentGuessesHisto/`;
export const mostRecentGuessesTimelinePath = `${firebaseRoot}/questionMostRecentGuessesTimeline/`;

export const starsPath = `${firebaseRoot}/questionStars/`;
export const commentsPath = `${firebaseRoot}/questionComments/`;
export const progressPath = `${firebaseRoot}/moduleProgress/`;
export const nNewAcaCouModQueComsPath = `${firebaseRoot}/newAcaCouModQueComs/`;
export const activityDACMPath = `${firebaseRoot}/activityDACM/`;
export const activityACMDPath = `${firebaseRoot}/activityACMD/`;
// HIERARCHY

// Academy
export const academySummariesPath = `${firebaseRoot}/academySummaries/`;
export const oneAcademyPathF = (academyId) =>
  `${firebaseRoot}/academies/${academyId}/`;
export const oneAcademyCourseSummariesPathF = (academyId) =>
  `${oneAcademyPathF(academyId)}courseSummaries/`;

// Course
export const oneCoursePathF = (academyId, courseId) =>
  `${firebaseRoot}/courses/${courseId}/`;
export const oneCourseModuleSummariesPathF = (academyId, courseId) =>
  `${oneCoursePathF(academyId, courseId)}moduleSummaries/`;

// Module
export const modulesPath = `${firebaseRoot}/modules/`;
export const oneModulePathF = (moduleId) =>
  `${firebaseRoot}/modules/${moduleId}/`;

// export const moduleSummariesRoot = moduleSummariesRootF("echo", "intro"); // TEMPORARY UNTIL WE REPLACE WITH INDIVIDUALISED, VARIABLE
// export const moduleSummariesRef = db.ref(moduleSummariesRoot);

export function writeUserP(userId, pathWithinUser, value) {
  return db.ref(usersPath).child(userId).child(pathWithinUser).set(value);
}

export function writeUserContactP(userId, pathWithinContact, value) {
  return db
    .ref(usersPath)
    .child(userId)
    .child("contactRW")
    .child(pathWithinContact)
    .set(value);
}

/*
 
    _____ _                 _    _____ _                             
   / ____| |               | |  / ____| |                            
  | |    | | ___  _   _  __| | | (___ | |_ ___  _ __ __ _  __ _  ___ 
  | |    | |/ _ \| | | |/ _` |  \___ \| __/ _ \| '__/ _` |/ _` |/ _ \
  | |____| | (_) | |_| | (_| |  ____) | || (_) | | | (_| | (_| |  __/
   \_____|_|\___/ \__,_|\__,_| |_____/ \__\___/|_|  \__,_|\__, |\___|
                                                           __/ |     
                                                          |___/      
 
*/

const storageRootRef = getStorage();
const assetsRootRef = firebaseCloudStorageRefFunction(storageRootRef, "assets"); // Images
// Twitter cards were "cards/" (version 1), then "h2/" (version 2), which the
// createStaticCard Cloud Function now writes (#50); see createStaticCardP.

// Keyed on the SHA1 hex of the image data URI. Holds the upload PROMISE, not
// its result, so a second paste of the same image while the first is still
// uploading shares that upload instead of starting another.
const imageUploadPromisesByUrlHash = {};

// From "CUIXIPING" answer at https://stackoverflow.com/questions/16968945/convert-base64-png-data-to-javascript-file-objects?noredirect=1&lq=1
// Return a promise that resolves with a File instance
function dataUriToFileP(url, filename, mimeType) {
  mimeType = mimeType || (url.match(/^data:([^;]+);/) || "")[1];
  return fetch(url) // It's not going to the internet, it is just reading the existing dataURI
    .then((res) => res.arrayBuffer())
    .then((buf) => new File([buf], filename, { type: mimeType }));
}

// storage.rules let only creators upload, through the isCreator custom claim
// (#50). A creator made before that claim existed doesn't have it yet, so ask
// the syncMyCreatorClaim function to set it, and fetch a token that carries
// it. If that fails, the upload still goes ahead and the rules decide.
async function ensureCreatorClaimP() {
  const user = auth.currentUser;
  if (!user) return;
  const { claims } = await user.getIdTokenResult();
  if (claims.isCreator === true) return;
  try {
    const result = await httpsCallable(getFunctions(), "syncMyCreatorClaim")();
    if (result.data && result.data.isCreator) await user.getIdToken(true);
  } catch {
    // The upload then fails if the rules need the claim, and the caller
    // reports that.
  }
}

// storage.rules refuse to replace an image (#50), so one already in the
// bucket, from an earlier session, is reused rather than uploaded again.
async function uploadImageUrlP(imageUrl, imageUrlHash) {
  const cloudStorageRef = firebaseCloudStorageRefFunction(
    assetsRootRef,
    `${imageUrlHash}.png`
  );
  let firebaseDownloadUrl;
  try {
    firebaseDownloadUrl = await getDownloadURL(cloudStorageRef);
  } catch (error) {
    if (error.code !== "storage/object-not-found") throw error;
    const fileObject = await dataUriToFileP(imageUrl, imageUrlHash, "image/png");
    await ensureCreatorClaimP();
    await uploadBytes(cloudStorageRef, fileObject);
    firebaseDownloadUrl = await getDownloadURL(cloudStorageRef);
  }
  if (isUsingFirebaseEmulator) {
    // The emulator has no storage.googleapis.com address to convert to. Its
    // download URL keeps one token, since an image is never replaced.
    return firebaseDownloadUrl;
  }
  return convertFirebaseCloudStorageUrlToGoogleCloudStorageUrl(
    firebaseDownloadUrl
  );
}

// Rejects if the upload fails, so the caller never receives undefined in
// place of a URL. Nothing awaits between checking and filling
// imageUploadPromisesByUrlHash, so two pastes of one image upload it once.
export async function uploadImageUrlIfNotAlreadyP(imageUrl) {
  const imageUrlHash = await getSha1HexFromStringP(imageUrl);
  if (
    !Object.prototype.hasOwnProperty.call(
      imageUploadPromisesByUrlHash,
      imageUrlHash
    )
  ) {
    imageUploadPromisesByUrlHash[imageUrlHash] = uploadImageUrlP(
      imageUrl,
      imageUrlHash
    ).catch((error) => {
      // Forget the failure, so that the next paste of this image retries
      delete imageUploadPromisesByUrlHash[imageUrlHash];
      throw error;
    });
  }
  return imageUploadPromisesByUrlHash[imageUrlHash];
}

// Has the createStaticCard Cloud Function write the "tweet this" page for a
// course, module or question, and resolves with its address on
// tweetorials.inspirion.org. The function reads the page's content from the
// database, so only identifiers are sent:
//   { level: "course" | "module" | "question", academyId, courseId, moduleId, questionId }
export async function createStaticCardP(cardIds) {
  const result = await httpsCallable(getFunctions(), "createStaticCard")(cardIds);
  return result.data.cardUrl;
}

export function isFirebaseCloudStorageUrl(url) {
  // This is the old URL to access it from, which requires a different token per upload, causing multiply-used images to break. And can't be cached.
  return (
    url.trim().startsWith(HTTPS + BUCKET_ADDRESS_VIA_FIREBASE_CLOUD) ||
    url.trim().startsWith(HTTP + BUCKET_ADDRESS_VIA_FIREBASE_CLOUD)
  );
}

export function convertFirebaseCloudStorageUrlToGoogleCloudStorageUrl(
  firebaseUrl
) {
  const withoutPrefix = firebaseUrl.replace(
    HTTPS + BUCKET_ADDRESS_VIA_FIREBASE_CLOUD,
    ""
  );
  const withoutPrefixOrSuffix = withoutQueryString(withoutPrefix);
  const googleCloudUrl =
    HTTPS + BUCKET_ADDRESS_VIA_GOOGLE_CLOUD + withoutPrefixOrSuffix;

  return googleCloudUrl;
}
